import React from "react"
import { observer } from "mobx-react-lite"

import {
  Blockquote,
  Text,
  Paper,
  Flex,
  Avatar,
  createStyles,
  Group,
  Badge,
  rem,
  Box,
  Button,
  TypographyStylesProvider, Accordion,
} from "@mantine/core"
import { IconNote, IconBrandWhatsapp, IconFlame, IconListDetails } from "@tabler/icons-react"
import { useMediaQuery } from "@mantine/hooks"
import { fDate, getInclinedMonth } from "shared/lib/dates"
import { API_URL } from "shared/config"
import { ParticipationRequestModal } from "../../features/participationRequestModal"
import { type ScheduleEvent } from "../../shared/types/scheduleEvent"

// ----------------------------------------------------------------------

const useStyles = createStyles((theme) => ({
  wrapper: {
    flex: 1,

    [theme.fn.smallerThan("md")]: {
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
  },

  paper: {
    marginTop: rem(64),
    border: `${rem(1)} solid ${theme.colors.gray[2]}`,
    position: "relative",
    borderRadius: theme.radius.lg,
    padding: rem(48),

    [theme.fn.smallerThan("sm")]: {
      padding: `${rem(48)} ${rem(24)}`,
      marginTop: rem(48),
    },

    [theme.fn.smallerThan("xs")]: {
      padding: `${theme.spacing.xl} ${theme.spacing.md}`,
    },
  },

  profession: {
    border: `${rem(1)} solid ${theme.colors.orange[2]}`,
    boxShadow: `${theme.fn.rgba(theme.colors.orange[7], 0.2)} 0px 8px 24px`,
  },

  desktopDate: {
    paddingLeft: rem(128),
    flexBasis: "40%",

    [theme.fn.smallerThan("lg")]: {
      paddingLeft: rem(96),
      flexBasis: "30%",
    },

    [theme.fn.smallerThan("md")]: {
      display: "none",
    },
  },

  mobileDate: {
    [theme.fn.largerThan("md")]: {
      display: "none",
    },
  },

  mobileDateText: {
    [theme.fn.smallerThan("sm")]: {
      fontSize: theme.headings.sizes.h1.fontSize,
    },
  },

  about: {
    [theme.fn.smallerThan("md")]: {
      alignItems: "center",
    },
  },

  level: {
    position: "absolute",
    top: 20,
    right: 48,

    [theme.fn.smallerThan("sm")]: {
      position: "static",
      marginBottom: theme.spacing.sm,
    },
  },

  avatar: {
    [theme.fn.smallerThan("md")]: {
      width: rem(140),
      minWidth: rem(140),
      height: rem(140),
    },
  },

  title: {
    [theme.fn.smallerThan("md")]: {
      textAlign: "center",
    },

    [theme.fn.smallerThan("sm")]: {
      fontSize: theme.headings.sizes.h2.fontSize,
    },
  },

  instructorContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.625rem",

    [theme.fn.smallerThan("sm")]: {
      flexWrap: "nowrap",
      flexDirection: "column",
      alignItems: "center",
      gap: "unset",
      marginTop: theme.spacing.sm,
      marginBottom: theme.spacing.sm,
    },
  },
}))

// ----------------------------------------------------------------------

export const ScheduleListElement = observer(function ScheduleListElement(props: ScheduleEvent) {
  const { classes, cx, theme } = useStyles()
  const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const {
    _id,
    level,
    profession,
    date,
    isOnline,
    instructor,
    title,
    program,
  } = props

  return (
    <Paper key={_id} className={cx(classes.paper, { [classes.profession]: !!profession })}>
      {level && (
        <Group position="center">
          <Badge size="lg" className={classes.level} color="gray">{`${
            level === "based" ? "Базовый" : "Продвинутый"
          } уровень`}</Badge>
        </Group>
      )}

      <Flex className={classes.wrapper} gap="xl">
        <Flex className={classes.desktopDate} align="flex-start" gap="xl">
          <Text fz={96} fw={900} lh={1}>
            {fDate(date, "DD")}
          </Text>
          <Flex direction="column" gap={4}>
            <Text fz={28} fw={700}>
              {getInclinedMonth(date)}
            </Text>

            {isOnline && (
              <Text fz="lg" c="green" fw={700} inherit>
                Онлайн
              </Text>
            )}
          </Flex>
        </Flex>

        <Flex className={classes.mobileDate} direction="column" align="center">
          <Text align="center" fz={48} fw={700} className={classes.mobileDateText}>
            {fDate(date, isTablet ? "D MMMM" : "DD MMMM")}
          </Text>

          {isOnline && (
            <Text fz="lg" c="green" fw={700} inherit>
              Онлайн
            </Text>
          )}
        </Flex>

        <Flex className={classes.wrapper} align="flex-start" gap="xl">
          {/*{profession ? (*/}
          {/*  <Avatar.Group spacing={isMobile ? 48 : 32}>*/}
          {/*    <Avatar*/}
          {/*      className={classes.avatar}*/}
          {/*      radius="50%"*/}
          {/*      size={96}*/}
          {/*      src={`${API_URL}/media/${instructor?.avatar}`}*/}
          {/*      alt={instructor?.name}*/}
          {/*      styles={{ image: { objectPosition: "50% 0" } }}*/}
          {/*    />*/}
          {/*    <Avatar color="blue" className={classes.avatar} radius="50%" size={96}>*/}
          {/*      +3*/}
          {/*    </Avatar>*/}
          {/*  </Avatar.Group>*/}
          {/*) : (*/}
            <Avatar
              className={classes.avatar}
              radius="50%"
              size={96}
              src={`${API_URL}/media/${instructor?.avatar}`}
              alt={instructor?.name}
              styles={{ image: { objectPosition: "50% 0" } }}
            />
          {/*)}*/}

          <Flex className={classes.about} direction="column" gap={4}>
            <Text fz={28} fw={700} className={classes.title}>
              {title}
            </Text>

            <Box className={classes.instructorContainer}>
              <Text fz="lg" fw={500} c="dimmed">
                {profession ? "Старший преподаватель:" : "Преподаватель:"}
              </Text>
              <Text fz="lg" fw={500} c="dimmed">
                {instructor?.name ?? "Ошибка получения данных"}
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Flex>

      <Group position="center" mt="xl">
        <Accordion
          radius="lg"
          variant="filled"
          chevronPosition="left"
          chevron={<IconListDetails/>}
          disableChevronRotation
          styles={{ control: { justifyContent: "center" }, label: { flex: "unset" } }}
        >
          <Accordion.Item value="customization">
            <Accordion.Control>Программа обучения</Accordion.Control>
            <Accordion.Panel>
              <TypographyStylesProvider>
                <Box dangerouslySetInnerHTML={{ __html: program }}/>
              </TypographyStylesProvider>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Group>

      {!!profession && (
        <Flex direction="column" align="center" mt="xl" gap="xl">
          <Blockquote
            p={0}
            cite={`Длительность обучения - ${profession.duration}`}
            icon={<IconFlame color="orange" size="1.5rem"/>}
          >
            {profession.description}
          </Blockquote>
        </Flex>
      )}

      <Group position="center" mt="xl">
        {/*<AmoCRMForm/>*/}
        <ParticipationRequestModal target={`Страница расписания. Элемент - ${title}`}>
          <Button
            component="div"
            leftIcon={<IconNote/>}
            variant="gradient"
            size={isMobile ? "sm" : "md"}
            radius="xl"
          >
            Подать заявку на участие
          </Button>
        </ParticipationRequestModal>

        <Button
          component="a"
          leftIcon={<IconBrandWhatsapp/>}
          variant="gradient"
          gradient={{ from: "teal", to: "lime", deg: 105 }}
          size={isMobile ? "sm" : "md"}
          radius="xl"
          target="_blank"
          href="https://api.whatsapp.com/send/?phone=79952048045&text&type=phone_number&app_absent=0"
        >
          Спросить в WhatsApp
        </Button>
      </Group>
    </Paper>
  )
})

